import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import Markdown from 'react-markdown';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import AddCommentIcon from '@material-ui/icons/AddComment';
import { styled } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { assistantAsk, assistantGetCredits } from '../../api/assistant';
import type { ChatMode } from '../../api/assistant';
import { companyGetInfo } from '../../api/company';
import { useScreenContext } from '../../hooks/use-screen-context';

interface Source {
  source: string;
  score: number;
  snippet: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
}

// External links open in a new tab; everything else renders as plain markdown.
const renderers = {
  link: (props) => {
    const { href, children, ...other } = props;
    if (!href || !href.startsWith('http')) {
      return <a href={href} {...other}>{children}</a>;
    }
    return (
      <a href={href} rel="nofollow noreferrer noopener" target="_blank" {...other}>
        {children}
      </a>
    );
  }
};

const MarkdownWrapper = styled('div')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  '& p': { fontSize: 14, lineHeight: 1.6, marginTop: 0, marginBottom: theme.spacing(1) },
  '& p:last-child': { marginBottom: 0 },
  '& li': { fontSize: 14, lineHeight: 1.6 },
  '& code': {
    fontFamily: 'Monaco, Consolas, monospace',
    fontSize: 13,
    backgroundColor: theme.palette.action.hover,
    padding: '1px 4px',
    borderRadius: 4
  },
  '& a': { color: theme.palette.primary.main }
}));

interface ChatWidgetProps {
  open: boolean;
  onClose: () => void;
}

export const ChatWidget: FC<ChatWidgetProps> = ({ open, onClose }) => {
  const [mode, setMode] = useState<ChatMode>('book');
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [usesOwnAPIKey, setUsesOwnAPIKey] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);

  const screen = useScreenContext();
  const { t } = useTranslation();

  // Refresh the credit balance whenever the widget is opened.
  useEffect(() => {
    if (open) {
      assistantGetCredits().then(setCredits);
    }
  }, [open]);

  // A company that has switched the assistant onto its own Anthropic/OpenAI key is
  // billed by that provider directly, so a credit balance is not the number to show
  // it. The company endpoint only reports which provider is active, never the key.
  useEffect(() => {
    if (!open) {
      return;
    }
    companyGetInfo()
      .then((company) =>
        setUsesOwnAPIKey(Boolean(company?.use_anthropic_api_key || company?.use_openai_api_key))
      )
      // 404 when the user has no company: they are on our key and our credits.
      .catch(() => setUsesOwnAPIKey(false));
  }, [open]);

  // Auto-scroll the thread to the latest message.
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const handleNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setError(null);
  };

  const handleSend = async () => {
    const question = input.trim();
    if (!question || sending) {
      return;
    }
    setError(null);
    setSending(true);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: question }]);

    const { data, err } = await assistantAsk(question, conversationId, {
      mode,
      context: mode === 'advice' ? screen.text : undefined
    });

    if (err) {
      const status = (err as any)?.response?.status;
      setError(
        status === 402
          ? "Vous n'avez plus de crédits pour interroger l'assistant."
          : 'Une erreur est survenue. Réessayez.'
      );
      setSending(false);
      return;
    }

    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: data.answer, sources: data.sources }
    ]);
    setCredits(data.credits_remaining);
    if (!conversationId && data.conversation_id) {
      setConversationId(data.conversation_id);
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) {
    return null;
  }

  // An empty balance only blocks when credits are actually charged: on the
  // company's own key the server neither gates nor deducts them.
  const blocked = !usesOwnAPIKey && credits !== null && credits <= 0;
  const placeholder =
    mode === 'advice'
      ? 'Demandez un conseil sur cet écran…'
      : 'Posez une question sur le livre IpOp…';

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 96,
        right: 24,
        zIndex: 1300,
        width: { xs: 'calc(100vw - 32px)', sm: 420 },
        height: { xs: 'calc(100vh - 160px)', sm: 560 },
        maxHeight: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Assistant IpOp
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            size="small"
            label={
              usesOwnAPIKey
                ? t('chat_own_api_key')
                : `Crédits : ${credits === null ? '…' : credits}`
            }
            color={blocked ? 'error' : 'default'}
            variant="outlined"
          />
          <Tooltip title="Nouvelle conversation">
            <IconButton size="small" onClick={handleNewConversation}>
              <AddCommentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Fermer">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Mode toggle */}
      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          fullWidth
          value={mode}
          onChange={(_e, v) => v && setMode(v)}
        >
          <ToggleButton value="book">Livre IpOp</ToggleButton>
          <ToggleButton value="advice">Conseils (écran)</ToggleButton>
        </ToggleButtonGroup>
        {mode === 'advice' && (
          <>
            <Tooltip title={screen.text || 'Aucune donnée détectée sur cet écran'}>
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                label={`Contexte : ${screen.label}`}
                sx={{ mt: 1, maxWidth: '100%' }}
              />
            </Tooltip>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              Raisonnement approfondi : ce mode consomme plus de crédits que la
              recherche dans le livre.
            </Typography>
          </>
        )}
      </Box>

      {/* Thread */}
      <Box ref={threadRef} sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
        {messages.length === 0 && !sending && (
          <Typography color="textSecondary" variant="body2" sx={{ mt: 4, textAlign: 'center' }}>
            {mode === 'advice'
              ? 'Posez une question : je croise le contexte de votre écran avec le livre IpOp.'
              : 'Posez une question sur le livre IpOp pour démarrer.'}
          </Typography>
        )}
        {messages.map((m, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              mb: 1.5
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                maxWidth: '85%',
                backgroundColor: m.role === 'user' ? 'primary.main' : 'background.paper',
                color: m.role === 'user' ? 'primary.contrastText' : 'text.primary'
              }}
            >
              {m.role === 'assistant' ? (
                <>
                  <MarkdownWrapper>
                    <Markdown escapeHtml renderers={renderers} source={m.content} />
                  </MarkdownWrapper>
                  {m.sources && m.sources.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {m.sources.map((s, j) => (
                        <Chip
                          key={j}
                          size="small"
                          label={`${s.source} (${Math.round(s.score * 100)}%)`}
                          title={s.snippet}
                        />
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {m.content}
                </Typography>
              )}
            </Paper>
          </Box>
        ))}
        {sending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <CircularProgress size={16} />
            <Typography variant="body2">L'assistant réfléchit…</Typography>
          </Box>
        )}
      </Box>

      {error && (
        <Typography color="error" variant="body2" sx={{ px: 2, py: 0.5 }}>
          {error}
        </Typography>
      )}

      {/* Input */}
      <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending || blocked}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={sending || !input.trim() || blocked}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};
